import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Tldraw,
  useEditor,
  getSnapshot,
  loadSnapshot,
  defaultHandleExternalTldrawContent,
  Editor,
  TLTldrawExternalContent,
} from 'tldraw';
import 'tldraw/tldraw.css';
import { FiMinimize2, FiMaximize2 } from 'react-icons/fi';
import { isEmpty, debounce } from 'lodash';
import { TLAssetStore } from 'tldraw';
import { uploadTldrawImageToCloudinary } from '@amit/composables';
import { isEqual } from 'lodash';
import { removeNullIds } from '@/utils';
import swal from 'sweetalert';
import type { TldrawSnapshot } from '@amit/types';
import { generateObjectId } from '@/utils';
type Props = {
  data?: { document: TldrawSnapshot };
  onchange?: (data: { document: TldrawSnapshot }) => void;
  isPointerEventsNone?: boolean;
  isFullscreen?: boolean;
  onShapesChange: (hasShapes: boolean) => void;
};

const MAX_FILE_SIZE_MB = 10;

export const cloudinaryAssetStore: TLAssetStore & {
  onUploadSuccess?: () => void;
} = {
  async upload(asset, file) {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      swal({
        title: 'קובץ גדול מדי',
        text: `נא להעלות קובץ בגודל עד ${MAX_FILE_SIZE_MB}MB בלבד. הקובץ שברשותך הוא ${fileSizeMB.toFixed(2)}MB.`,
        icon: 'warning',
        buttons: false,
      });
      throw new Error('File size exceeds limit');
    }

    const url = await uploadTldrawImageToCloudinary(file);
    if (cloudinaryAssetStore.onUploadSuccess) {
      cloudinaryAssetStore.onUploadSuccess(); // 🔔 notify
    }
    return { src: url };
  },

  resolve(asset) {
    return asset.props.src;
  },
};

const InsideOfContext = ({
  data,
  onchange,
  isPointerEventsNone,
  isFullscreen,
  onZoomToFit,
  onShapesChange,
}: Props & { onZoomToFit?: () => void }) => {
  const editor = useEditor();
  const [hasShapes, setHasShapes] = useState(false);

  useEffect(() => {
    editor.updateInstanceState({ isReadonly: isPointerEventsNone });
  }, [editor, isPointerEventsNone, isFullscreen]);

  useEffect(() => {
    if (!editor || !data || isEmpty(data?.document?.store)) return;
    const snapshot = removeNullIds(data).document;
    snapshot.store['document:document'].meta ||= {};

    const currentSnapshot = getSnapshot(editor.store);
    const hasSameData = isEqual(
      currentSnapshot.document?.store,
      snapshot.store,
    );
    if (!hasSameData) {
      loadSnapshot(editor.store, snapshot);
      setTimeout(() => {
        editor.zoomToFit();
        editor.resetZoom();
      }, 100);
    }
  }, [editor, data]);

  const debouncedChangeHandler = debounce(() => {
    setHasShapes([...editor.getCurrentPageShapeIds()].length > 0);

    const { document } = getSnapshot(editor.store);
    const cleaned = removeNullIds(document);
    onchange({ document: cleaned });
  }, 300);

  useEffect(() => {
    if (!editor || !onchange) return;

    const cleanup = editor.store.listen(
      ({ changes }) => {
        if (!changes) return;
        debouncedChangeHandler();
      },
      { source: 'user' },
    );

    // Initial check for shapes
    setHasShapes([...editor.getCurrentPageShapeIds()].length > 0);

    return () => {
      cleanup();
      debouncedChangeHandler.cancel();
    };
  }, [editor, onchange]);

  useEffect(() => {
    if (isFullscreen && onZoomToFit && editor) {
      onZoomToFit(); // Trigger zoomToFit when entering fullscreen
    }
  }, [isFullscreen, onZoomToFit, editor]);

  useEffect(() => {
    if (onShapesChange) {
      onShapesChange(hasShapes);
    }
  }, [hasShapes, onShapesChange]);

  return null;
};

const TldrawReactWrapper = ({ data, onchange }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasShapes, setHasShapes] = useState(false);

  const persistenceKey = useMemo(() => generateObjectId(), []);

  // Watch for hasUploaded changes and enter fullscreen
  useEffect(() => {
    cloudinaryAssetStore.onUploadSuccess = () => {
      swal({
        title: 'התמונה הועלתה בהצלחה!',
        text: 'התמונה נוספה ללוח הציור',
        icon: 'success',
        buttons: false,
      });
    };

    return () => {
      cloudinaryAssetStore.onUploadSuccess = undefined;
    };
  }, []);

  const enterFullscreen = () => {
    containerRef.current?.requestFullscreen?.();
    setIsFullscreen(true); // Explicitly set isFullscreen
    setTimeout(() => {
      if (onZoomToFitRef.current) {
        onZoomToFitRef.current(); // Call zoomToFit after entering fullscreen
      }
    }, 100);
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement === containerRef.current) {
      document.exitFullscreen?.(); // Exit fullscreen only if it's the React component
      setIsFullscreen(false); // Explicitly set isFullscreen
    }
  };

  const isPointerEventsNone = useMemo(() => {
    if (!containerRef.current) return false;
    const computedStyle = window.getComputedStyle(
      containerRef.current.parentElement,
    );
    return computedStyle.pointerEvents === 'none';
  }, [containerRef.current, isFullscreen, data, isHovered]);

  const onZoomToFitRef = useRef<(() => void) | null>(null); // Ref to store zoomToFit callback

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        document.fullscreenElement === containerRef.current
      ) {
        exitFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  const isFullscreenRef = useRef(isFullscreen);
  useEffect(() => {
    isFullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  const handleScopedTldrawPaste = (
    editor: Editor,
    { content, point }: TLTldrawExternalContent,
  ) => {
    if (!isFullscreenRef.current) {
      return;
    }
    defaultHandleExternalTldrawContent(editor, { content, point });
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '30rem',
        position: 'relative',
        direction: 'ltr',
        pointerEvents: isFullscreen ? 'all' : 'inherit',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Tldraw
        persistenceKey={persistenceKey}
        acceptedImageMimeTypes={[
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/svg+xml',
        ]}
        acceptedVideoMimeTypes={[]}
        maxAssetSize={11 * 1024 * 1024}
        onMount={editor => {
          editor.updateInstanceState({ isReadonly: true });
          onZoomToFitRef.current = () => editor.zoomToFit();
          editor.registerExternalContentHandler('tldraw', content =>
            handleScopedTldrawPaste(editor, content),
          );
        }}
        assets={cloudinaryAssetStore}
      >
        <InsideOfContext
          data={data}
          onchange={onchange}
          isPointerEventsNone={isPointerEventsNone}
          isFullscreen={isFullscreen}
          onZoomToFit={onZoomToFitRef.current}
          onShapesChange={setHasShapes}
        />
      </Tldraw>

      {/* Overlay */}
      {!isFullscreen && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#F9FAFB',
            zIndex: 998,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '2rem',
            cursor: 'pointer',
            pointerEvents: 'all',
            transition: 'opacity 0.3s',
            opacity: 0.95,
          }}
        >
          <div
            className="font-anomaliaBold"
            style={{
              textAlign: 'center',
              color: '#1E232A',
              fontWeight: 700,
              fontSize: '26px',
              font: 'Anomalia',
            }}
          >
            !לחצו על כפתור המסך המלא כדי להתחיל
          </div>
        </div>
      )}

      {/* Fullscreen toggle button */}
      {
        <button
          onClick={isFullscreen ? exitFullscreen : enterFullscreen}
          className="full-screen-button cursor-pointer absolute left-4 p-3 rounded-md bg-white z-[10] full-screen-shadow hover:scale-[1.1]"
          title="מסך מלא"
          style={{ pointerEvents: 'all', top: '48px', zIndex: '999' }}
        >
          {isFullscreen ? (
            <FiMinimize2 size={20} className="stroke-secondary-900" />
          ) : (
            <FiMaximize2 size={20} className="stroke-secondary-900" />
          )}
        </button>
      }
    </div>
  );
};

export default TldrawReactWrapper;
